import axios from 'axios'

import { Account, Notification, NotificationStatus, Organization, User } from './models'
import {
  getAccountIdFromLocalStorage,
  getOrgIdFromUrl,
  setAccountIdToLocalStorage,
  setOrgIdToLocalStorage,
  updateAllAccountToLocalStorage
} from './utils/helpers'

let userCache: User | null = undefined

export type AccountsAndOrganizations = {
  userAccounts: Account[]
  organizations: Organization[]
}

export class DataService {
  public async getCurrentUser (): Promise<User | null> {
    if (userCache !== undefined) {
      return Promise.resolve(userCache)
    }
    const resp = await axios.post('/users/cookie')
    const payload = resp.data
    if (payload && payload.decrypted_cookie && payload.decrypted_cookie.user) {
      if (payload.is_refreshed === true) {
        location.reload()
      }
      userCache = {
        name: payload.decrypted_cookie.user.name,
        email: payload.decrypted_cookie.user.email,
      }
      if ((window as any).dataLayer) {
        // window dataLayer is the object used to send GTM additional values.
        // We are passing `is_qc_user` so that GTM can make a decision to initialize fullstory.
        (window as any).dataLayer.push({
          event: 'userLoaded',
          isQcUser: userCache.email.indexOf('@quantcast.com') > -1
        })
      }
    } else {
      userCache = null
    }
    return userCache
  }

  public getAccountsAndOrganizations (selectedAccount: Partial<Account> | null): Promise<AccountsAndOrganizations> {
    const variables: { id?: number[], pcodes?: string[], orgId?: number[] } = {}
    const params: string[] = []
    let query = `
      userAccounts {
        id pCode name timeZone currencyCode countryCode permission hideMediaCost legalReviewStatus
      }
      organizations {
        name
        id
        actions: allowedActions(actions: [MANAGE_USERS, MANAGE_ACCOUNTS, MANAGE_ROLES, MANAGE_SUPPLY])
        accounts { name, id, pcode }
      }
    `

    const orgIdFromUrl = getOrgIdFromUrl()
    if (orgIdFromUrl) {
      query += `orgIdFromUrl: organizations(id: $orgId) {
        name
        id
        actions: allowedActions(actions: [MANAGE_USERS, MANAGE_ACCOUNTS, MANAGE_ROLES, MANAGE_SUPPLY])
        accounts { name, id, pcode }
      }`
      params.push('$orgId:[Long!]')
      variables.orgId = [orgIdFromUrl]
    }

    if (selectedAccount) {
      query += `accountFromUrl: accounts(id: $id, pcodes: $pcodes) {
        id dataset {
          id
        } name timeZone currencyCode countryCode hideMediaCost legalReviewStatus
      }`
      params.push('$id:[Long!]')
      params.push('$pcodes:[String!]')
      if (selectedAccount.id) {
        variables.id = [selectedAccount.id]
      } else {
        variables.pcodes = [selectedAccount.pCode]
      }
    }
    if (params.length > 0) {
      query = `query UserAccountsQuery(${params.join(',')}){` + query + '}'
    } else {
      query = 'query UserAccountsQuery {' + query + '}'
    }

    return this.getCurrentUser().then(user => {
      return axios({
        url: '/ads/apiv2/manager/graphql',
        method: 'post',
        data: {
          operationName: `UserAccountsQuery`,
          query: query,
          variables: variables
        }
      }).then(resp => {

        const accounts = resp.data!.data!.userAccounts || []
        if (resp.data!.data!.accountFromUrl && resp.data!.data!.accountFromUrl.length > 0) {
          let accountFromUrl = resp.data!.data!.accountFromUrl
          if (accountFromUrl === null || accountFromUrl.length === 0) {
            return null
          }
          accountFromUrl = accountFromUrl[0]
          const pCode: string = accountFromUrl.dataset.id
          if (pCode.length === 13) {
            accountFromUrl.pCode = 'p-' + pCode
          } else {
            accountFromUrl.pCode = pCode
          }
          delete accountFromUrl.dataset
          accountFromUrl.permission = 'READ'

          if (!accounts.some(acc => acc.id === selectedAccount.id || acc.pCode === selectedAccount.pCode)) {
            accounts.push(accountFromUrl)
          }
        }

        const organizations = resp.data!.data!.organizations || []

        organizations.forEach((org) => {
          if (org.accounts && org.accounts.length > 0) {
            org.accounts = org.accounts.map((account) => account.pcode)
          }
        })

        if (resp.data!.data!.orgIdFromUrl && resp.data!.data!.orgIdFromUrl.length > 0) {
          const org = resp.data!.data!.orgIdFromUrl[0]
          if (org.accounts && org.accounts.length > 0) {
            org.accounts = org.accounts.map((account) => account.pcode)
          }
          if (!organizations.some(o => o.id === org.id)) {
            organizations.push(org)
          }
        }

        return {
          userAccounts: resp.data!.data!.userAccounts || [],
          organizations: organizations
        }
      })
    })
  }

  public getAccount (id?: number, pcode?: string): Promise<Account> {
    return this.getCurrentUser().then(user => {
      const variables: { id?: number[], pcodes?: string[] } = {}
      if (id) {
        variables.id = [id]
      } else {
        variables.pcodes = [pcode]
      }
      return axios({
        url: '/ads/apiv2/manager/graphql',
        method: 'post',
        data: {
          operationName: `UserAccountQuery`,
          query: `query UserAccountQuery($id:[Long!], $pcodes:[String!]){
            accounts(id: $id, pcodes: $pcodes) {
              id dataset {
                id
              } name timeZone currencyCode countryCode hideMediaCost legalReviewStatus
            }
          }`,
          variables: variables
        }
      }).then(resp => {
        let acc = resp.data!.data!.accounts
        if (acc === null || acc.length === 0) {
          return null
        }
        acc = acc[0]
        const pCode: string = acc.dataset.id
        if (pCode.length === 13) {
          acc.pCode = 'p-' + pCode
        } else {
          acc.pCode = pCode
        }
        delete acc.dataset
        acc.permission = 'READ'
        return acc
      })
    })
  }

  public updateSelectedAccount (acc: Account): void {
    updateAllAccountToLocalStorage(acc.id)
    setAccountIdToLocalStorage(acc.id)

    if ((acc as any).organization) {
      setOrgIdToLocalStorage((acc as any).organization.id || (acc as any).organization.orgId)
    }
  }

  public getSelectedAccount (accs: Account[]): Account | null {
    if (!accs || !accs.length) {
      return null
    }
    const selectedId = getAccountIdFromLocalStorage()
    if (!selectedId) {
      return accs[0]
    }
    const selectedIdNum = parseInt(selectedId, 10)
    const selectedAcc = accs.find(acc => acc.id === selectedIdNum)
    return selectedAcc || accs[0]
  }

  public getNotifications (acc: Account): Promise<Notification[]> {
    return this.getCurrentUser().then(_ => {
      return axios({
        url: '/ads/apiv2/manager/graphql',
        method: 'post',
        data: {
          operationName: `NotificationsQuery`,
          query: `query NotificationsQuery($accountId: Long!) {
            notifications(accountId: $accountId) {
              id viewed status title message callToActionText callToActionCallback createdDate {
                seconds
                nanos
              }
            }
          }`,
          variables: {
            accountId: acc.id
          }
        }
      }).then(resp => {
        if (!resp.data?.data?.notifications) {
          return []
        }
        return resp.data!.data!.notifications.map((notif: any) => {
          notif.createdDate = new Date(notif.createdDate.seconds * 1000)
          return notif as Notification
        })
      })
    })
  }

  public updateNotificationStatus (notifications: Notification[], status: NotificationStatus): Promise<any> {
    return this.getCurrentUser().then(_ => {
      return axios({
        url: '/ads/apiv2/manager/graphql',
        method: 'post',
        data: {
          operationName: `UpdateNotificationStatus`,
          query: `mutation UpdateNotificationStatus($ids: [Long!]!, $status: NotificationStatus!) {
            updateNotificationStatus(ids: $ids, status: $status)
          }`,
          variables: {
            ids: notifications.map((notif) => notif.id),
            status
          }
        }
      })
    })
  }

  public markNotificationAsRead (notifications: Notification[]): Promise<any> {
    return this.getCurrentUser().then(_ => {
      return axios({
        url: '/ads/apiv2/manager/graphql',
        method: 'post',
        data: {
          operationName: `UpdateNotificationViewed`,
          query: `mutation UpdateNotificationViewed($ids: [Long!]!) {
            updateNotificationViewed(ids: $ids)
          }`,
          variables: {
            ids: notifications.map((notif) => notif.id)
          }
        }
      })
    })
  }
}
